import MoreVertIcon from '@mui/icons-material/MoreVert'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useState } from 'react'

import { ApiError, api, type Role, type User } from '../api/client'
import { useAuth } from '../auth/useAuth'
import ConfirmDialog from '../components/ConfirmDialog'
import OneTimePassword, { CopyButton } from '../components/OneTimePassword'
import { generatePassword } from '../lib/password'
import { relativeTime } from '../lib/time'

/** Which dialog is open, and what it is about. */
type Pending =
  | { kind: 'create' }
  | { kind: 'created'; username: string; password: string }
  | { kind: 'reset'; user: User }
  | { kind: 'reset-done'; user: User; password: string }
  | { kind: 'delete'; user: User }
  | { kind: 'role'; user: User; role: Role }
  | { kind: 'active'; user: User; active: boolean }
  | null

function describe(error: unknown): string {
  if (error instanceof ApiError) return error.message
  return 'Something went wrong. Try again.'
}

export default function UsersPage() {
  const { user: me, refresh } = useAuth()
  const [users, setUsers] = useState<User[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState<Pending>(null)
  const [busy, setBusy] = useState(false)
  const [menuFor, setMenuFor] = useState<{ user: User; anchor: HTMLElement } | null>(null)

  const load = useCallback(async () => {
    try {
      setUsers(await api.listUsers())
      setError(null)
    } catch (err) {
      // A 403 here means this session is no longer a sysadmin -- another
      // sysadmin demoted us while the page was open. Re-reading the session
      // makes the app route us away instead of leaving a screen where every
      // click fails with a role error.
      if (err instanceof ApiError && err.status === 403) {
        await refresh()
        return
      }
      setError(describe(err))
    }
  }, [refresh])

  useEffect(() => {
    void load()
  }, [load])

  /** Runs an action, then reloads. Keeps every mutation's error handling identical. */
  async function act(work: () => Promise<void>) {
    setBusy(true)
    try {
      await work()
      await load()
    } catch (err) {
      setError(describe(err))
    } finally {
      setBusy(false)
    }
  }

  const closeMenu = () => setMenuFor(null)

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h5" component="h2">
          Users
        </Typography>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          disabled={busy}
          onClick={() => setPending({ kind: 'create' })}
        >
          Add User
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper>
        {users === null ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <TableContainer>
            {/* The last row's cells would otherwise draw a border a pixel above
                the Paper's rounded bottom edge, which reads as a stray line.
                Rows above it keep theirs -- they are separating something. */}
            <Table size="small" sx={{ '& tbody tr:last-of-type td': { borderBottom: 0 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last signed in</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2">{user.username}</Typography>
                        {user.id === me?.id && <Chip size="small" label="You" />}
                        {user.must_change_password && (
                          <Tooltip title="Must choose a new password at next sign-in">
                            <Chip size="small" variant="outlined" label="Password pending" />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={user.role}
                        color={user.role === 'sysadmin' ? 'primary' : 'default'}
                        variant={user.role === 'sysadmin' ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color={user.is_active ? 'text.primary' : 'text.secondary'}
                      >
                        {user.is_active ? 'Active' : 'Disabled'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {user.last_login_at ? relativeTime(user.last_login_at) : 'Never'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {/* No actions on your own row. Every one of them either
                          is refused by the server (delete), signs you out
                          mid-dialog (disable, password reset -- which revokes
                          every session including this one, losing the
                          generated password before it can be copied), or
                          leaves this page unable to load (demoting yourself
                          out of the sysadmin role). Your own password is
                          changed from the account menu; the rest is another
                          sysadmin's job, which is also the only situation in
                          which the server would permit it. */}
                      {user.id === me?.id ? (
                        <Tooltip title="Change your own password on My account. Another sysadmin handles the rest.">
                          <Typography variant="caption" color="text.secondary" sx={{ pr: 1 }}>
                            —
                          </Typography>
                        </Tooltip>
                      ) : (
                        <IconButton
                          size="small"
                          disabled={busy}
                          aria-label={`Actions for ${user.username}`}
                          onClick={(event) => setMenuFor({ user, anchor: event.currentTarget })}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Menu anchorEl={menuFor?.anchor ?? null} open={menuFor !== null} onClose={closeMenu}>
        {menuFor && (
          <MenuItem
            onClick={() => {
              setPending({
                kind: 'role',
                user: menuFor.user,
                role: menuFor.user.role === 'sysadmin' ? 'admin' : 'sysadmin',
              })
              closeMenu()
            }}
          >
            <ListItemText>
              {menuFor.user.role === 'sysadmin' ? 'Make admin' : 'Make sysadmin'}
            </ListItemText>
          </MenuItem>
        )}
        {menuFor && (
          <MenuItem
            onClick={() => {
              setPending({
                kind: 'active',
                user: menuFor.user,
                active: !menuFor.user.is_active,
              })
              closeMenu()
            }}
          >
            <ListItemText>
              {menuFor.user.is_active ? 'Disable account' : 'Enable account'}
            </ListItemText>
          </MenuItem>
        )}
        {menuFor && (
          <MenuItem
            onClick={() => {
              setPending({ kind: 'reset', user: menuFor.user })
              closeMenu()
            }}
          >
            <ListItemText>Reset password</ListItemText>
          </MenuItem>
        )}
        <Divider />
        {menuFor && (
          <MenuItem
            onClick={() => {
              setPending({ kind: 'delete', user: menuFor.user })
              closeMenu()
            }}
          >
            <ListItemText primaryTypographyProps={{ color: 'error' }}>Delete user</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <CreateDialog
        open={pending?.kind === 'create'}
        busy={busy}
        onCancel={() => setPending(null)}
        onCreate={(username, role) => {
          void act(async () => {
            const password = generatePassword()
            await api.createUser(username, password, role)
            setPending({ kind: 'created', username, password })
          })
        }}
      />

      <Dialog open={pending?.kind === 'created'} onClose={() => setPending(null)} maxWidth="sm">
        <DialogTitle>{pending?.kind === 'created' && `${pending.username} added`}</DialogTitle>
        <DialogContent>
          {pending?.kind === 'created' && (
            <OneTimePassword
              password={pending.password}
              instruction={`Send it to ${pending.username} so they can sign in.`}
            />
          )}
        </DialogContent>
        <DialogActions>
          {pending?.kind === 'created' && <CopyButton password={pending.password} />}
          <Button variant="contained" onClick={() => setPending(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={pending?.kind === 'reset'}
        title="Reset password?"
        body={
          pending?.kind === 'reset'
            ? `A new password will be generated for ${pending.user.username}. They will be signed out everywhere immediately, and must choose their own password at next sign-in.`
            : ''
        }
        confirmLabel="Reset Password"
        busy={busy}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          if (pending?.kind !== 'reset') return
          const { user } = pending
          void act(async () => {
            const password = generatePassword()
            await api.resetUserPassword(user.id, password)
            setPending({ kind: 'reset-done', user, password })
          })
        }}
      />

      <Dialog open={pending?.kind === 'reset-done'} onClose={() => setPending(null)} maxWidth="sm">
        <DialogTitle>
          {pending?.kind === 'reset-done' && `New password for ${pending.user.username}`}
        </DialogTitle>
        <DialogContent>
          {pending?.kind === 'reset-done' && (
            <OneTimePassword
              password={pending.password}
              instruction={`Send it to ${pending.user.username}. Their old sessions have already been signed out.`}
            />
          )}
        </DialogContent>
        <DialogActions>
          {pending?.kind === 'reset-done' && <CopyButton password={pending.password} />}
          <Button variant="contained" onClick={() => setPending(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={pending?.kind === 'delete'}
        title="Delete this user?"
        destructive
        body={
          pending?.kind === 'delete'
            ? `${pending.user.username} will be removed and signed out everywhere. What they did stays in the audit log. Disabling the account instead is reversible; this is not.`
            : ''
        }
        confirmLabel="Delete"
        busy={busy}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          if (pending?.kind !== 'delete') return
          const { user } = pending
          void act(async () => {
            await api.deleteUser(user.id)
            setPending(null)
          })
        }}
      />

      <ConfirmDialog
        open={pending?.kind === 'role'}
        title={
          pending?.kind === 'role' && pending.role === 'sysadmin' ? 'Make sysadmin?' : 'Make admin?'
        }
        body={
          pending?.kind === 'role'
            ? pending.role === 'sysadmin'
              ? `${pending.user.username} will be able to add and remove accounts, reset passwords, and read the audit log.`
              : `${pending.user.username} will no longer be able to manage accounts or read the audit log. They can still order covers.`
            : ''
        }
        confirmLabel="Change Role"
        busy={busy}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          if (pending?.kind !== 'role') return
          const { user, role } = pending
          void act(async () => {
            await api.updateUser(user.id, { role })
            setPending(null)
          })
        }}
      />

      <ConfirmDialog
        open={pending?.kind === 'active'}
        title={
          pending?.kind === 'active' && pending.active ? 'Enable account?' : 'Disable account?'
        }
        body={
          pending?.kind === 'active'
            ? pending.active
              ? `${pending.user.username} will be able to sign in again.`
              : `${pending.user.username} will be signed out everywhere and cannot sign in until re-enabled. This is reversible.`
            : ''
        }
        confirmLabel={pending?.kind === 'active' && pending.active ? 'Enable' : 'Disable'}
        busy={busy}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          if (pending?.kind !== 'active') return
          const { user, active } = pending
          void act(async () => {
            await api.updateUser(user.id, { is_active: active })
            setPending(null)
          })
        }}
      />
    </Stack>
  )
}

function CreateDialog({
  open,
  busy,
  onCancel,
  onCreate,
}: {
  open: boolean
  busy: boolean
  onCancel: () => void
  onCreate: (username: string, role: Role) => void
}) {
  const [username, setUsername] = useState('')
  const [role, setRole] = useState<Role>('admin')

  // Reset between openings, so a cancelled attempt does not prefill the next.
  useEffect(() => {
    if (open) {
      setUsername('')
      setRole('admin')
    }
  }, [open])

  // Mirrors the server's rule, which is what actually enforces it.
  const valid = /^[a-zA-Z0-9._-]{3,64}$/.test(username.trim())

  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} fullWidth maxWidth="xs">
      <DialogTitle>Add User</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Username"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            error={username.length > 0 && !valid}
            helperText="Letters, numbers, dot, dash or underscore. At least 3 characters."
            autoFocus
            fullWidth
          />
          <TextField
            select
            label="Role"
            value={role}
            onChange={(event) => setRole(event.target.value as Role)}
            SelectProps={{ native: true }}
            fullWidth
          >
            <option value="admin">admin — orders covers</option>
            <option value="sysadmin">sysadmin — also manages accounts</option>
          </TextField>
          <Typography variant="caption" color="text.secondary">
            A password will be generated and shown to you once.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!valid || busy}
          onClick={() => onCreate(username.trim().toLowerCase(), role)}
        >
          Add User
        </Button>
      </DialogActions>
    </Dialog>
  )
}
